import React, { useState } from 'react';

const CountriesTable = ({ countries, onDeleteItem, onUpdateStatus }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

    const handleStatusChange = (itemId, newStatus) => {
        onUpdateStatus(itemId, newStatus);
    };

    const handleSort = (key) => {
        let newDirection;

        if (sortConfig.key !== key) {
            // First click on a new column - ascending
            newDirection = 'asc';
        } else if (sortConfig.direction === 'asc') {
            // Second click on same column - descending
            newDirection = 'desc';
        } else if (sortConfig.direction === 'desc') {
            // Third click on same column - no sorting
            newDirection = null;
        } else {
            // If somehow direction is null, start with ascending
            newDirection = 'asc';
        }

        setSortConfig({
            key: newDirection ? key : null,
            direction: newDirection
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey || !sortConfig.direction) {
            return null; // No icon when not sorted
        }
        return sortConfig.direction === 'asc'
            ? <span className="sort-icon sort-icon-asc text-blue-600 ml-2 font-bold text-lg">▲</span>
            : <span className="sort-icon sort-icon-desc text-blue-600 ml-2 font-bold text-lg">▼</span>;
    };

    const getHeaderClasses = (columnKey) => {
        let baseClasses = "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-blue-100 sortable-header select-none";

        if (sortConfig.key === columnKey) {
            if (sortConfig.direction === 'asc') {
                baseClasses += " sorted-asc bg-blue-50";
            } else if (sortConfig.direction === 'desc') {
                baseClasses += " sorted-desc bg-blue-50";
            }
        } else {
            baseClasses += " unsorted";
        }

        return baseClasses;
    };

    const sortedCountries = React.useMemo(() => {
        if (!sortConfig.key || !sortConfig.direction) return countries;

        return [...countries].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Handle different data types
            if (sortConfig.key === 'isFavorite') {
                aValue = a.isFavorite ? 1 : 0;
                bValue = b.isFavorite ? 1 : 0;
            } else if (sortConfig.key === 'tripCost') {
                aValue = parseInt(a.tripCost) || 0;
                bValue = parseInt(b.tripCost) || 0;
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [countries, sortConfig]);

    const totalCost = sortedCountries.reduce((sum, item) => sum + (parseInt(item.tripCost) || 0), 0);

    return (
        <div className="table-section bg-white rounded-lg shadow-lg p-6 mb-8 border-l-4 border-blue-500" id="countries-table-section">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Travel Destinations</h3>
                <div className="total-cost bg-green-100 px-4 py-2 rounded-lg border-2 border-green-300">
                    <span className="text-sm font-medium text-green-700">Total Cost: </span>
                    <span className="text-xl font-bold text-green-800" id="total-cost" data-test-id="total-cost">
                        ${totalCost.toLocaleString()}
                    </span>
                </div>
            </div>
            <div className="table-wrapper bg-white rounded-lg shadow">
                <table className="w-full table" id="countries-table" data-test-id="countries-table">
                    <thead className="bg-blue-50 table-header">
                        <tr className="header-row">
                            <th
                                className={getHeaderClasses('destination')}
                                id="header-destination"
                                data-test-id="header-destination"
                                onClick={() => handleSort('destination')}
                            >
                                <div className="flex items-center">
                                    Destination
                                    {getSortIcon('destination')}
                                </div>
                            </th>
                            <th
                                className={getHeaderClasses('travelType')}
                                id="header-travel-type"
                                onClick={() => handleSort('travelType')}
                            >
                                <div className="flex items-center">
                                    Travel Type
                                    {getSortIcon('travelType')}
                                </div>
                            </th>
                            <th
                                className={getHeaderClasses('tripCost')}
                                id="header-cost"
                                data-test-id="header-cost"
                                onClick={() => handleSort('tripCost')}
                            >
                                <div className="flex items-center">
                                    Cost ($)
                                    {getSortIcon('tripCost')}
                                </div>
                            </th>
                            <th
                                className={getHeaderClasses('status')}
                                id="header-status"
                                data-test-id="header-status"
                                onClick={() => handleSort('status')}
                            >
                                <div className="flex items-center">
                                    Status
                                    {getSortIcon('status')}
                                </div>
                            </th>
                            <th
                                className={getHeaderClasses('isFavorite')}
                                id="header-favorite"
                                onClick={() => handleSort('isFavorite')}
                            >
                                <div className="flex items-center">
                                    Favorite
                                    {getSortIcon('isFavorite')}
                                </div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider unsorted" id="header-actions">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 table-body">
                        {sortedCountries.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                    No travel destinations added yet. Click "Add Travel Item" to get started!
                                </td>
                            </tr>
                        ) : (
                            sortedCountries.map((item, index) => (
                                <tr key={item.id} className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`} id={`travel-row-${item.id}`} data-test-id={`travel-row-${item.id}`}>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-destination">
                                        <span className="font-medium">{item.destination}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-travel-type" data-test-id={`cell-travel-type-${item.id}`}>
                                        <span className={`px-2 py-1 text-sm rounded-full ${item.travelType === 'Business' ? 'bg-blue-100 text-blue-800' :
                                            item.travelType === 'Leisure' ? 'bg-green-100 text-green-800' :
                                                item.travelType === 'Adventure' ? 'bg-orange-100 text-orange-800' :
                                                    item.travelType === 'Cultural' ? 'bg-purple-100 text-purple-800' :
                                                        'bg-gray-100 text-gray-800'
                                            }`}>
                                            {item.travelType || 'Not specified'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-cost" data-test-id={`cell-cost-${item.id}`}>
                                        <span className="font-medium text-green-600">
                                            ${parseInt(item.tripCost) || 0}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-status" data-test-id={`cell-status-${item.id}`}>
                                        <select
                                            value={item.status}
                                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                            className={`px-2 py-1 text-sm rounded-full border-0 cursor-pointer status-dropdown ${item.status === 'Planned' ? 'bg-yellow-100 text-yellow-800' :
                                                item.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                    item.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'
                                                }`}
                                            id={`status-dropdown-${item.id}`}
                                            data-test-id={`status-dropdown-${item.id}`}
                                        >
                                            <option value="Planned">Planned</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Cancelled">Cancelled</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-favorite" data-test-id={`cell-favorite-${item.id}`}>
                                        <span className={`px-3 py-1 text-sm rounded-full font-medium ${item.isFavorite ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {item.isFavorite ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap cell cell-actions">
                                        <button
                                            onClick={() => onDeleteItem(item.id)}
                                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-weight-bold btn-delete border-2 border-red-800"
                                            id={`delete-btn-${item.id}`}
                                            data-test-id={`delete-button-${item.id}`}
                                            style={{
                                                backgroundColor: '#dc2626',
                                                borderColor: '#991b1b',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CountriesTable;